/* eslint-disable max-lines */
/* eslint-disable max-params */
/* eslint-disable max-statements */
/* eslint-disable complexity */
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import {
  describe,
  PropTypes,
  useMemoMergePropsWithDefault,
  useValidateTypescriptPropTypes,
} from '@elliemae/ds-props-helpers';
import { setCursorPosition, useCallbackAfterRender } from '../utils/index.js';
import { useNumberMaskDefaultProps, useNumberMaskPropTypes, type UseNumberMaskT } from '../react-desc-prop-types.js';
import { useNumberMaskName } from '../DSMaskDefinitions.js';

const addThousandsSeparator = (n: string, separator: string) => n.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

const conformValue = (
  rawValue: string,
  cursorPos: number,
  lastKeyCode: string,
  opts: Omit<UseNumberMaskT.Props, 'valueSetter'>,
  finalize = false,
): [string, number] => {
  const {
    includeThousandsSeparator = true,
    allowNegative = false,
    prefix = '',
    suffix = '',
    decimalPlaces = 2,
    decimalRequired = false,
  } = opts;
  const THOUSANDSSEPARATOR = ',';

  let dotIdx = -1;
  // allow minus sign before and after the preffix
  let finalPrefix = [prefix?.length, 0].includes(rawValue.indexOf('-')) && allowNegative ? '-' : '';

  // CHANGE: when decimals are not allowed (decimalPlaces === 0), ignore everything after the first dot
  let seenInvalidDot = false;

  let maskedValue = String(rawValue)
    .split('')
    .filter((char, idx) => {
      if (seenInvalidDot) return false;

      const isDigit = char >= '0' && char <= '9';

      if (char === '.') {
        if (decimalPlaces) {
          const validChar = dotIdx === -1;
          if (validChar) dotIdx = idx;
          return validChar;
        }
        // decimals not allowed: drop the dot and everything after it
        seenInvalidDot = true;
        return false;
      }

      // we take only the first dot (handled above); otherwise only digits
      return isDigit;
    });

  if (prefix && maskedValue.length) finalPrefix += prefix;

  // REMOVE FOLLOWING ZEROS IN INTEGER PART AFTER LEADER ZERO NUMBER
  while (maskedValue.length >= 2 && maskedValue[0] === '0' && maskedValue[1] !== '.') {
    maskedValue = maskedValue.splice(1);
  }
  dotIdx = maskedValue.findIndex((char) => char === '.');

  let integer = maskedValue.slice(0, dotIdx !== -1 ? dotIdx : maskedValue.length);
  const decimal = maskedValue.slice(dotIdx, dotIdx + decimalPlaces + 1);
  // ADDING THOUSANDS SEPARATOR
  if (includeThousandsSeparator) {
    integer = addThousandsSeparator(integer.join(''), THOUSANDSSEPARATOR).split('');
  }

  let zeroWasAdded = false;

  // MERGIN INT AND DECIMAL IF NECCESSARY
  if (dotIdx !== -1 && !(lastKeyCode === 'Backspace' && decimal.length === 1)) {
    if (integer.length) {
      maskedValue = [...integer, ...decimal];
    } else {
      // if theres no integer add a zero to the left of the dot to cover .23 => 0.23 case
      maskedValue = ['0', ...decimal];
      zeroWasAdded = true;
    }
  } else {
    // If decimal is required we add a dot after integer if exists
    maskedValue = decimalRequired && integer.length ? [...integer, '.'] : integer;
  }

  // remove orphans prefix for example "-$"
  if (lastKeyCode === 'Backspace' && maskedValue.length === 0) {
    finalPrefix = '';
  }

  // SET REAL CURSOR POSITION AFTER ADDING DOT and COMMAS SEPARATORS
  let maskedPos = 0;

  // 0 leading case we move the cursor to continue typing
  if (maskedValue[maskedPos] === '0' && cursorPos === 1 + prefix.length) maskedPos += 1;

  if (zeroWasAdded) maskedPos += 1;

  for (let i = 0; i < cursorPos; i += 1) {
    if (maskedValue[maskedPos] === THOUSANDSSEPARATOR) maskedPos += 1;
    if (maskedValue[maskedPos] === rawValue[i]) maskedPos += 1;
  }

  maskedPos += finalPrefix.length;

  // --- FINALIZATION STEP (pads decimals if requested) ---
  if (finalize && decimalPlaces > 0 && decimalRequired) {
    const hasDot = maskedValue.includes('.');
    if (!hasDot) {
      if (maskedValue.length !== 0) {
        maskedValue = [...maskedValue, '.', ...Array<string>(decimalPlaces).fill('0')];
        if (cursorPos >= 0) maskedPos += 1 + decimalPlaces;
      }
    } else {
      const dotAt = maskedValue.indexOf('.');
      const decimalsCount = maskedValue.length - dotAt - 1;
      if (decimalsCount < decimalPlaces) {
        const toAdd = decimalPlaces - decimalsCount;
        maskedValue = [...maskedValue, ...Array<string>(toAdd).fill('0')];
      } else if (decimalsCount > decimalPlaces) {
        maskedValue = [...maskedValue.slice(0, dotAt + 1 + decimalPlaces)];
      }
    }
  }

  const finalSuffix = maskedValue.length ? suffix : '';
  const maskedValueString = finalPrefix + maskedValue.join('') + finalSuffix;
  return [maskedValueString, maskedPos];
};

export const useNumberMask: ((args: UseNumberMaskT.Props) => UseNumberMaskT.OutputT) & { displayName: string } = (
  props,
) => {
  const propsWithDefault = useMemoMergePropsWithDefault<UseNumberMaskT.InternalProps>(props, useNumberMaskDefaultProps);
  useValidateTypescriptPropTypes(propsWithDefault, useNumberMaskPropTypes, useNumberMaskName);

  const {
    valueSetter,
    onChange: userOnChange,
    onKeyDown: userOnKeyDown,
    onBlur: userOnBlur,
    ...opts
  } = propsWithDefault;

  const lastKeyCode = useRef('Unidentified');

  const scheduleAfterRender = useCallbackAfterRender(true);

  // Some times, the value of the mask is not gonna change (user types invalid char for example)
  // This makes the input to go from "correct" -> "incorrect" -> "correct"
  // But since the input is controlled, the input does not re-render
  // This makes the input go mumbo-jumbo and places the cursor in the end, because we kinda changed the value async
  // Solution for this is to trigger a re-render, we do this with a set state call in the on change event
  const [, setKey] = useState(0);

  const onBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const maskedValue = e.target.value;

      const {
        decimalRequired = true,
        decimalPlaces = 2,
        suffix = '',
        // prefix = '',
        // min = -Infinity,
        // max = Infinity,
      } = opts;
      // const value = Number(e.target.value.replace(/[,`${prefix}${suffix}`]/g, ''));
      // if (value < min) valueSetter(`${prefix}${min.toString()}${suffix}`);
      // if (value > max) valueSetter(`${prefix}${max.toString()}${suffix}`);
      const dotIdx = maskedValue.indexOf('.');
      const minusIndex = maskedValue.indexOf('-');

      const suffixIndex = suffix.length ? maskedValue.indexOf(suffix) : -1;

      if (decimalRequired) {
        if (dotIdx > -1) {
          const decimal = maskedValue.slice(dotIdx + 1, suffix.length ? -suffix.length : maskedValue.length);

          let zerosRest = '';
          while (decimal.length + zerosRest.length !== decimalPlaces) {
            zerosRest += '0';
          }

          valueSetter((suffix.length ? maskedValue.slice(0, suffixIndex) : maskedValue) + zerosRest + suffix);
        }
      } else if (maskedValue.length - suffix.length - 1 === dotIdx) {
        // removing orphans dots
        valueSetter(maskedValue.replace('.', ''));
      } else if (maskedValue.length - 1 === minusIndex) {
        // removing orphans minus
        valueSetter(maskedValue.replace('-', ''));
      }
      if (userOnBlur) userOnBlur(e);
    },
    [userOnBlur, valueSetter, opts],
  );

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      lastKeyCode.current = e.code;
      if (userOnKeyDown) userOnKeyDown(e);
    },
    [userOnKeyDown],
  );

  const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const [mask, cursorPos] = conformValue(
        e.target.value,
        e.target.selectionEnd ?? 0,
        lastKeyCode.current,
        opts as UseNumberMaskT.Props,
      );

      // Strip formatting characters and prefix/suffix
      const numericValue = Number(mask.replace(opts.prefix, '').replace(opts.suffix, '').replace(/,/g, ''));

      // If the value is below min or above max, do not update to that value.
      if (opts.min !== undefined && numericValue < opts.min) {
        // Option 1: Force the value to the minimum
        valueSetter(`${opts.prefix}${opts.min}${opts.suffix}`);
        scheduleAfterRender(() => setCursorPosition(e.target, cursorPos));
        return;
        // Option 2: Prevent the update (i.e. ignore the change) until a valid number is entered.
        // return;
      }
      if (opts.max !== undefined && numericValue > opts.max) {
        // Force the value to the maximum
        valueSetter(`${opts.prefix}${opts.max}${opts.suffix}`);
        scheduleAfterRender(() => setCursorPosition(e.target, cursorPos));
        return;
      }

      // Only update if the numeric value is within the valid range.
      valueSetter((prevMask) => {
        if (mask === prevMask) setKey((key) => key + 1);
        return mask;
      });
      if (userOnChange) userOnChange(e, mask);
      scheduleAfterRender(() => setCursorPosition(e.target, cursorPos));
    },
    [valueSetter, scheduleAfterRender, userOnChange, opts],
  );

  return {
    onKeyDown,
    onChange,
    onBlur,
  };
};

export const getNumberMaskedValue = (value: string, opts: Omit<UseNumberMaskT.Props, 'valueSetter'>) => {
  const [maskedValue] = conformValue(value, value.length - 1, '', opts, true);
  return maskedValue;
};

useNumberMask.displayName = useNumberMaskName;
export const UseNumberMaskWithSchema = describe(useNumberMask);
UseNumberMaskWithSchema.propTypes = useNumberMaskPropTypes;
UseNumberMaskWithSchema.returnType = {
  onKeyDown: PropTypes.func.description('On key down event for input').isRequired,
  onChange: PropTypes.func.description('On change event for input').isRequired,
  onBlur: PropTypes.func.description('On blur event for input').isRequired,
};
